import React,{useEffect,useMemo,useState}from"react";
import{
  Box,Button,FormControl,InputLabel,MenuItem,Paper,Select,
  Table,TableBody,TableCell,TableContainer,TableHead,
  TablePagination,TableRow,TextField,Typography,useMediaQuery
}from"@mui/material";
import{useTheme}from"@mui/material/styles";
import{useLocation,useNavigate,useParams}from"react-router-dom";
import Sidebar from"./AdminPanel/Sidebar";
import TopBar from"./AdminPanel/TopBar";
import PayrollForm from"./AdminPayroll/PayrollForm";
import PayrollPayments from"./AdminPayroll/PayrollPayments";
import Overtime from"./AdminPayroll/Overtime";
import Advances from"./AdminPayroll/Advances";
import{useDashboardTheme}from"../../context/DashboardThemeContext";
import{useAuth}from"../../context/AuthContext";
import{useAlert}from"../../context/AlertContext";

const API_BASE="/api/purchases/nomina";
const SIDEBAR_EVENT="yumim:sidebar-change";
const SIDEBAR_STATE_KEY="__yumimDesktopSidebarOpen";

function getDesktopSidebarOpen(){
  if(typeof window==="undefined")return true;
  if(typeof window[SIDEBAR_STATE_KEY]!=="boolean"){
    window[SIDEBAR_STATE_KEY]=true;
  }
  return window[SIDEBAR_STATE_KEY];
}

function updateDesktopSidebarOpen(value){
  if(typeof window==="undefined")return;
  const current=getDesktopSidebarOpen();
  const next=typeof value==="function"?Boolean(value(current)):Boolean(value);
  if(current===next)return;
  window[SIDEBAR_STATE_KEY]=next;
  window.dispatchEvent(new Event(SIDEBAR_EVENT));
}

function getToken(){
  if(typeof window==="undefined")return"";
  return window.localStorage.getItem("accessToken")||window.localStorage.getItem("access")||"";
}

function requestHeaders(token){
  return token?{Authorization:`Bearer ${token}`}:{};
}

function userCan(user,code){
  if(!user)return false;
  if(user.role==="super_admin"||user.role==="admin")return true;
  return Array.isArray(user.permissions)&&user.permissions.includes(code);
}

function money(value){
  const number=Number(String(value??"0").replace(",","."));
  return Number.isFinite(number)?number.toFixed(2):"0.00";
}

function numberValue(value){
  const number=Number(String(value??"0").replace(",","."));
  return Number.isFinite(number)?number:0;
}

function formatDate(value){
  if(!value)return"-";
  const parts=String(value).split("-");
  return parts.length===3?`${parts[2]}/${parts[1]}/${parts[0]}`:String(value);
}

function employeeName(employee){
  return employee?.razon_social||employee?.nombre_comercial||"-";
}

function parseError(data){
  const error=data?.error||data?.detail||data?.mensaje;
  if(typeof error==="string")return error;
  if(Array.isArray(error))return error.join(" ");
  if(error&&typeof error==="object"){
    return Object.values(error).flat().join(" ");
  }
  return"No se pudo procesar la solicitud.";
}

function getNetAmount(item){
  if(item?.resumen?.neto!==undefined&&item?.resumen?.neto!==null){
    return numberValue(item.resumen.neto);
  }
  return Math.max(
    0,
    numberValue(item?.total)-numberValue(item?.anticipos_posteriores)
  );
}

function getPaidAmount(item){
  if(item?.resumen?.total_pagado!==undefined&&item?.resumen?.total_pagado!==null){
    return numberValue(item.resumen.total_pagado);
  }
  return numberValue(item?.total_pagado);
}

function getPendingAmount(item){
  if(item?.resumen?.saldo_pendiente!==undefined&&item?.resumen?.saldo_pendiente!==null){
    return numberValue(item.resumen.saldo_pendiente);
  }
  return numberValue(item?.saldo_pendiente);
}

export default function AdminPayroll(){
  const muiTheme=useTheme();
  const isMobile=useMediaQuery(muiTheme.breakpoints.down("md"));
  const[desktopOpen,setDesktopOpen]=useState(getDesktopSidebarOpen);
  const[mobileOpen,setMobileOpen]=useState(false);
  const[empresaId,setEmpresaId]=useState("");
  const[empresaLoading,setEmpresaLoading]=useState(true);

  const{dashboardTheme}=useDashboardTheme();
  const{user}=useAuth();
  const{showError,showSuccess,showInfo}=useAlert();

  const navigate=useNavigate();
  const location=useLocation();
  const{payrollId}=useParams();

  const token=getToken();
  const path=location.pathname;

  const isOvertime=path==="/admin-panel/nomina/horas-extras";
  const isAdvances=path==="/admin-panel/nomina/anticipos";
  const isCreate=path==="/admin-panel/nomina/nueva";
  const isPayments=Boolean(payrollId)&&path.endsWith("/pagos");
  const isDetail=Boolean(payrollId)&&!isPayments&&!isOvertime&&!isAdvances;
  const isRoles=!isOvertime&&!isAdvances&&!isCreate&&!isDetail&&!isPayments;

  const canView=userCan(user,"compras.view");
  const canCreate=userCan(user,"compras.create");
  const canEdit=userCan(user,"compras.edit");
  const canDelete=userCan(user,"compras.delete");

  useEffect(()=>{
    const syncSidebar=()=>{
      setDesktopOpen(getDesktopSidebarOpen());
    };

    window.addEventListener(SIDEBAR_EVENT,syncSidebar);
    syncSidebar();

    return()=>{
      window.removeEventListener(SIDEBAR_EVENT,syncSidebar);
    };
  },[]);

  useEffect(()=>{
    setMobileOpen(false);
  },[location.pathname]);

  const sidebarOpen=isMobile?mobileOpen:desktopOpen;

  const handleToggleSidebar=()=>{
    if(isMobile){
      setMobileOpen(previous=>!previous);
      return;
    }
    updateDesktopSidebarOpen(previous=>!previous);
  };

  const handleCloseSidebar=()=>{
    if(isMobile){
      setMobileOpen(false);
      return;
    }
    updateDesktopSidebarOpen(false);
  };

  useEffect(()=>{
    let active=true;

    const loadEmpresa=async()=>{
      try{
        setEmpresaLoading(true);

        const response=await fetch("/api/enterprise/empresa/obtener/",{
          credentials:"include",
          headers:requestHeaders(token)
        });

        const data=await response.json();

        if(!active)return;

        if(!response.ok){
          showError(parseError(data));
          setEmpresaId("");
          return;
        }

        if(!data?.empresa?.id){
          showError("No existe una empresa configurada.");
          setEmpresaId("");
          return;
        }

        setEmpresaId(data.empresa.id);
      }catch{
        if(active){
          setEmpresaId("");
          showError("Error de conexión al cargar la empresa.");
        }
      }finally{
        if(active)setEmpresaLoading(false);
      }
    };

    loadEmpresa();

    return()=>{active=false;};
  },[token]);

  const outlinedButtonSx={
    minHeight:36,
    px:2,
    borderRadius:"10px",
    textTransform:"none",
    fontWeight:600,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const smallButtonSx={
    minHeight:30,
    px:1.4,
    borderRadius:"8px",
    textTransform:"none",
    fontWeight:600,
    fontSize:12,
    color:dashboardTheme.button_color,
    borderColor:dashboardTheme.button_color,
    boxShadow:"none",
    "&:hover":{
      borderColor:dashboardTheme.button_color,
      bgcolor:`${dashboardTheme.button_color}12`,
      boxShadow:"none"
    }
  };

  const smallContainedButtonSx={
    ...dashboardTheme.buttonStyles.contained,
    minHeight:30,
    px:1.4,
    borderRadius:"8px",
    textTransform:"none",
    fontWeight:600,
    fontSize:12,
    boxShadow:"none"
  };

  const dangerButtonSx={
    ...smallButtonSx,
    color:"#D32F2F",
    borderColor:"#D32F2F",
    "&:hover":{
      borderColor:"#D32F2F",
      bgcolor:"rgba(211,47,47,0.08)",
      boxShadow:"none"
    }
  };

  const selectMenuProps={
    disableScrollLock:true,
    PaperProps:{
      elevation:0,
      sx:{
        mt:.5,
        bgcolor:dashboardTheme.card_background,
        color:dashboardTheme.text_color,
        border:`1px solid ${dashboardTheme.border_color}`,
        boxShadow:"none"
      }
    }
  };

  const sharedProps={
    empresaId,
    token,
    canView,
    canCreate,
    canEdit,
    canDelete,
    dashboardTheme,
    navigate,
    showError,
    showSuccess,
    showInfo,
    outlinedButtonSx,
    smallButtonSx,
    smallContainedButtonSx,
    dangerButtonSx,
    selectMenuProps,
    parseError,
    isMobile
  };

  return(
    <Box sx={{
      minHeight:"100dvh",
      width:"100%",
      display:"flex",
      bgcolor:dashboardTheme.dashboard_background,
      color:dashboardTheme.text_color,
      overflowX:"hidden"
    }}>
      <Sidebar open={sidebarOpen} onClose={handleCloseSidebar}/>

      <Box sx={{
        flex:1,
        minWidth:0,
        minHeight:"100dvh",
        display:"flex",
        flexDirection:"column"
      }}>
        <TopBar
          sidebarOpen={sidebarOpen}
          onToggleSidebar={handleToggleSidebar}
        />

        <Box component="main" sx={{
          flex:1,
          minWidth:0,
          width:"100%",
          boxSizing:"border-box",
          px:{xs:1.5,sm:2.5,lg:3},
          py:{xs:2,sm:2.5,lg:3}
        }}>
          {empresaLoading&&(
            <Typography sx={{
              py:4,
              textAlign:"center",
              fontSize:14,
              fontWeight:400,
              color:dashboardTheme.muted_text_color
            }}>
              Cargando...
            </Typography>
          )}

          {!empresaLoading&&!empresaId&&(
            <Typography sx={{
              py:4,
              textAlign:"center",
              fontSize:14,
              color:dashboardTheme.muted_text_color
            }}>
              No se pudo cargar la empresa.
            </Typography>
          )}

          {!empresaLoading&&empresaId&&isRoles&&(
            <PayrollList {...sharedProps}/>
          )}

          {!empresaLoading&&empresaId&&(isCreate||isDetail)&&(
            <PayrollForm
              {...sharedProps}
              payrollId={payrollId}
              isCreate={isCreate}
            />
          )}

          {!empresaLoading&&empresaId&&isPayments&&(
            <PayrollPayments
              {...sharedProps}
              payrollId={payrollId}
            />
          )}

          {!empresaLoading&&empresaId&&isOvertime&&(
            <Overtime {...sharedProps}/>
          )}

          {!empresaLoading&&empresaId&&isAdvances&&(
            <Advances {...sharedProps}/>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function PayrollList({
  empresaId,
  token,
  canView,
  canCreate,
  canEdit,
  dashboardTheme,
  navigate,
  showError,
  smallButtonSx,
  smallContainedButtonSx,
  selectMenuProps,
  isMobile
}){
  const[items,setItems]=useState([]);
  const[loading,setLoading]=useState(false);
  const[search,setSearch]=useState("");
  const[estado,setEstado]=useState("todos");
  const[fechaDesde,setFechaDesde]=useState("");
  const[fechaHasta,setFechaHasta]=useState("");
  const[page,setPage]=useState(0);

  const rowsPerPage=isMobile?10:30;

  useEffect(()=>{
    if(!empresaId||!canView){
      setItems([]);
      return;
    }

    let active=true;
    const controller=new AbortController();

    const delay=setTimeout(async()=>{
      try{
        setLoading(true);

        const params=new URLSearchParams({
          empresa_id:String(empresaId)
        });

        if(search.trim())params.set("q",search.trim());
        if(estado!=="todos")params.set("estado",estado);
        if(fechaDesde)params.set("fecha_desde",fechaDesde);
        if(fechaHasta)params.set("fecha_hasta",fechaHasta);

        const response=await fetch(`${API_BASE}/?${params.toString()}`,{
          credentials:"include",
          headers:requestHeaders(token),
          signal:controller.signal
        });

        const data=await response.json();

        if(!active)return;

        if(!response.ok){
          showError(parseError(data));
          setItems([]);
          return;
        }

        setItems(Array.isArray(data.results)?data.results:[]);
      }catch(error){
        if(error.name==="AbortError")return;

        if(active){
          setItems([]);
          showError("Error de conexión al cargar los roles de pago.");
        }
      }finally{
        if(active)setLoading(false);
      }
    },350);

    return()=>{
      active=false;
      clearTimeout(delay);
      controller.abort();
    };
  },[empresaId,token,canView,search,estado,fechaDesde,fechaHasta]);

  useEffect(()=>{
    setPage(0);
  },[search,estado,fechaDesde,fechaHasta,rowsPerPage]);

  const visibleItems=useMemo(()=>{
    const start=page*rowsPerPage;
    return items.slice(start,start+rowsPerPage);
  },[items,page,rowsPerPage]);

  const estadoLabel=value=>{
    if(value==="BORRADOR")return"Borrador";
    if(value==="REGISTRADO")return"Registrado";
    if(value==="ANULADO")return"Anulado";
    return value||"-";
  };

  const pagoLabel=value=>{
    if(value==="PAGADO")return"Pagado";
    if(value==="PARCIAL")return"Parcial";
    return"Pendiente";
  };

  return(
    <>
      <Box sx={{
        display:"flex",
        alignItems:{xs:"stretch",sm:"center"},
        justifyContent:"space-between",
        flexDirection:{xs:"column",sm:"row"},
        gap:1.5,
        mb:2
      }}>
        <Box>
          <Typography sx={{
            fontSize:{xs:24,sm:26,md:30},
            fontWeight:600,
            color:dashboardTheme.primary_color,
            lineHeight:1.2
          }}>
            Roles de pago
          </Typography>

          <Typography sx={{
            fontSize:13,
            fontWeight:400,
            color:dashboardTheme.muted_text_color,
            mt:.5
          }}>
            Cierre mensual y registro de pagos de empleados
          </Typography>
        </Box>

        {canCreate&&(
          <Button
            variant="contained"
            onClick={()=>navigate("/admin-panel/nomina/nueva")}
            sx={{
              ...dashboardTheme.buttonStyles.contained,
              minHeight:36,
              px:2,
              borderRadius:"10px",
              fontWeight:600,
              textTransform:"none",
              boxShadow:"none"
            }}
          >
            Generar rol mensual
          </Button>
        )}
      </Box>

      <Paper elevation={0} sx={dashboardTheme.tableStyles.wrapper}>
        <Box sx={{
          ...dashboardTheme.tableStyles.toolbar,
          display:"flex",
          flexWrap:"wrap",
          alignItems:"center",
          gap:1
        }}>
          <TextField
            size="small"
            label="Buscar empleado o concepto"
            value={search}
            onChange={event=>setSearch(event.target.value)}
            sx={{
              width:{xs:"100%",sm:320,md:390},
              ...dashboardTheme.inputStyles
            }}
          />

          <FormControl size="small" sx={{
            minWidth:{xs:"100%",sm:155},
            ...dashboardTheme.inputStyles
          }}>
            <InputLabel>Estado</InputLabel>
            <Select
              label="Estado"
              value={estado}
              onChange={event=>setEstado(event.target.value)}
              MenuProps={selectMenuProps}
            >
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="BORRADOR">Borrador</MenuItem>
              <MenuItem value="REGISTRADO">Registrado</MenuItem>
              <MenuItem value="ANULADO">Anulado</MenuItem>
            </Select>
          </FormControl>

          <TextField
            size="small"
            type="date"
            label="Desde"
            value={fechaDesde}
            onChange={event=>setFechaDesde(event.target.value)}
            InputLabelProps={{shrink:true}}
            sx={{
              minWidth:{xs:"100%",sm:155},
              ...dashboardTheme.inputStyles
            }}
          />

          <TextField
            size="small"
            type="date"
            label="Hasta"
            value={fechaHasta}
            onChange={event=>setFechaHasta(event.target.value)}
            InputLabelProps={{shrink:true}}
            sx={{
              minWidth:{xs:"100%",sm:155},
              ...dashboardTheme.inputStyles
            }}
          />
        </Box>

        <Box sx={dashboardTheme.tableStyles.tableArea}>
          <TableContainer sx={dashboardTheme.tableStyles.container}>
            <Table size="small" sx={dashboardTheme.tableStyles.table}>
              <TableHead>
                <TableRow sx={dashboardTheme.tableStyles.headRow}>
                  <TableCell sx={{
                    ...dashboardTheme.tableStyles.headCell,
                    minWidth:190
                  }}>
                    Empleado
                  </TableCell>

                  <TableCell sx={dashboardTheme.tableStyles.headCell}>
                    Período
                  </TableCell>

                  <TableCell sx={dashboardTheme.tableStyles.headCell}>
                    Estado
                  </TableCell>

                  <TableCell sx={dashboardTheme.tableStyles.headCell}>
                    Pago
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={dashboardTheme.tableStyles.headCell}
                  >
                    Neto
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={dashboardTheme.tableStyles.headCell}
                  >
                    Pagado
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={dashboardTheme.tableStyles.headCell}
                  >
                    Saldo
                  </TableCell>

                  <TableCell
                    align="right"
                    sx={dashboardTheme.tableStyles.headCell}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {visibleItems.map(item=>{
                  const neto=getNetAmount(item);
                  const pagado=getPaidAmount(item);
                  const saldo=getPendingAmount(item);

                  return(
                    <TableRow
                      key={item.id}
                      hover
                      sx={{
                        ...dashboardTheme.tableStyles.bodyRow,
                        cursor:"pointer"
                      }}
                      onDoubleClick={()=>navigate(`/admin-panel/nomina/${item.id}`)}
                    >
                      <TableCell>
                        <Typography sx={{
                          fontSize:13.5,
                          fontWeight:600,
                          color:dashboardTheme.text_color,
                          lineHeight:1.25
                        }}>
                          {employeeName(item.empleado)}
                        </Typography>

                        {item.empleado?.identificacion&&(
                          <Typography sx={{
                            fontSize:11.5,
                            fontWeight:400,
                            color:dashboardTheme.muted_text_color,
                            mt:.3
                          }}>
                            {item.empleado.identificacion}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{
                        whiteSpace:"nowrap",
                        fontSize:12.5
                      }}>
                        {formatDate(item.periodo_desde)}
                        {" - "}
                        {formatDate(item.periodo_hasta)}
                      </TableCell>

                      <TableCell>
                        <Typography sx={{
                          fontSize:12.5,
                          fontWeight:500,
                          color:item.estado==="ANULADO"
                            ?"#D32F2F"
                            :dashboardTheme.text_color
                        }}>
                          {estadoLabel(item.estado)}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography sx={{
                          fontSize:12.5,
                          fontWeight:500,
                          color:item.estado_pago==="PARCIAL"
                            ?"#D97706"
                            :dashboardTheme.text_color
                        }}>
                          {pagoLabel(item.estado_pago)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                        <Typography sx={{
                          fontSize:13,
                          fontWeight:700,
                          color:dashboardTheme.text_color
                        }}>
                          ${money(neto)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                        ${money(pagado)}
                      </TableCell>

                      <TableCell align="right" sx={{whiteSpace:"nowrap"}}>
                        <Typography sx={{
                          fontSize:13,
                          fontWeight:600,
                          color:saldo>0
                            ?"#D97706"
                            :dashboardTheme.text_color
                        }}>
                          ${money(saldo)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Box sx={{
                          display:"flex",
                          justifyContent:"flex-end",
                          gap:.7
                        }}>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={event=>{
                              event.stopPropagation();
                              navigate(`/admin-panel/nomina/${item.id}`);
                            }}
                            sx={smallButtonSx}
                          >
                            {item.estado==="BORRADOR"&&canEdit
                              ?"Editar"
                              :"Ver"}
                          </Button>

                          {item.estado==="REGISTRADO"&&(
                            <Button
                              variant="contained"
                              size="small"
                              onClick={event=>{
                                event.stopPropagation();
                                navigate(`/admin-panel/nomina/${item.id}/pagos`);
                              }}
                              sx={smallContainedButtonSx}
                            >
                              Pagos
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {!loading&&visibleItems.length===0&&canView&&(
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={dashboardTheme.tableStyles.emptyCell}
                    >
                      No hay roles de pago registrados
                    </TableCell>
                  </TableRow>
                )}

                {loading&&(
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={dashboardTheme.tableStyles.emptyCell}
                    >
                      Cargando roles de pago...
                    </TableCell>
                  </TableRow>
                )}

                {!canView&&(
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={dashboardTheme.tableStyles.emptyCell}
                    >
                      No tienes permisos para consultar la nómina
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <TablePagination
          component="div"
          count={items.length}
          page={page}
          onPageChange={(_event,newPage)=>setPage(newPage)}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[]}
          labelDisplayedRows={({from,to,count})=>`${from}-${to} de ${count}`}
          sx={dashboardTheme.tableStyles.pagination}
        />
      </Paper>
    </>
  );
}